import { Container } from '@purpur/library';
import styled, { css } from 'styled-components';

import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';
import { NAVIGATION_OFFSET_CSS_VARIABLE } from '@telialt/utils/src/NavigationUtils/constants';

import { BlockCtaData } from './model';

type BlockCtaColorOption = BlockCtaData['colorOption'];

type BlockProps = {
    $boxed?: boolean;
    $centered?: boolean;
    $split?: boolean;
    $colorOption?: BlockCtaColorOption;
};

const isLightPurple = ($colorOption?: BlockCtaColorOption) => $colorOption === 'color-light-purple';

const toneOnToneSecondaryText = css`
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p,
    li {
        color: ${getPurpurColor('textToneOnToneSecondary')};
    }
`;

export const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('100')};
    flex: 1 1 0;
    min-width: 0;
`;

export const DescriptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('100')};
`;

export const SplitContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('300')};
    flex: 1 1 0;
    min-width: 0;
`;

export const ButtonsWrapper = styled.div`
    display: flex;
    gap: ${getPurpurSpacing('200')};
    flex-direction: column;

    ${purpurMedia('md')`
        flex-direction: row;
        align-items: flex-start;
    `};
`;

const defaultBoxedStyles = css`
    background-color: ${getPurpurColor('backgroundSecondary')};
    padding: ${getPurpurSpacing('400')};
    border-radius: ${getPurpurBorder('radiusMd')};
    border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderStrong')};
`;

/* Title + description only — keep primary button / arrow white (currentColor). */
const lightPurpleBoxedStyles = css`
    background-color: ${getPurpurColor('backgroundToneOnToneSecondary')};
    padding: ${getPurpurSpacing('300')} ${getPurpurSpacing('200')};
    border-radius: ${getPurpurBorder('radiusLg')};
    border: none;

    ${purpurMedia('md')`
        padding: ${getPurpurSpacing('300')} ${getPurpurSpacing('400')};
    `}

    ${TitleWrapper},
    ${DescriptionWrapper} {
        ${toneOnToneSecondaryText}
    }

    ${SplitContentWrapper} {
        > *:not(${ButtonsWrapper}) {
            ${toneOnToneSecondaryText}
        }
    }
`;

export const StyledBlockCta = styled.div<BlockProps>`
    scroll-margin-top: var(${NAVIGATION_OFFSET_CSS_VARIABLE});
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('400')};

    ${({ $boxed, $colorOption }) =>
        $boxed && (isLightPurple($colorOption) ? lightPurpleBoxedStyles : defaultBoxedStyles)}

    ${({ $centered }) =>
        $centered &&
        css`
            align-items: center;
            text-align: center;
        `}

    ${({ $split }) =>
        $split &&
        css`
            gap: ${getPurpurSpacing('200')};

            ${purpurMedia('lg')`
                flex-direction: row;
                align-items: center;
                gap: ${getPurpurSpacing('300')};
            `}
        `}
`;

export const StyledBlockCtaContainer = styled(Container)`
    padding-top: ${getPurpurSpacing('400')};
    padding-bottom: ${getPurpurSpacing('400')};

    ${purpurMedia('md')`
        padding-top: ${getPurpurSpacing('600')};
        padding-bottom: ${getPurpurSpacing('600')};
    `};
`;
